// 2026-10-23 addition (clinician invite-only build, Piece 6) — admin-only
// page for creating sales-rep accounts. Mirrors AdminClinicians.js's own
// shell/table/dialog conventions (same navy+gold admin chrome, same
// `/admin/users` role-agnostic listing reused and filtered client-side,
// same create-dialog pattern) rather than inventing a new one.
import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
} from "@mui/material";
import { Logout, Add as AddIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatEST } from "../dateUtils";
import ThemeToggle from "./ThemeToggle";
import { extractErrorDetail } from "./validators";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", gold: "#c9a96e" };

// 2026-10-23 addition (sales-rep lab-brand routing) — hand-duplicated in
// every place this app needs the same two options/labels (same
// convention as _normalize_phone's own hand-duplicated-helper
// precedent), so there's never a differently-worded label anywhere.
// Keep these two exact strings consistent with main.py's own
// LAB_BRAND_DISPLAY_NAMES.
const LAB_BRAND_OPTIONS = [
  { value: "infinity", label: "Infinity Labs" },
  { value: "apex", label: "APEX Labs" },
];

function NewSalesRepDialog({ open, onClose, onCreated, token }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", labBrand: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleClose = () => {
    if (submitting) return;
    setForm({ name: "", phone: "", email: "", labBrand: "" });
    setError(null); setSuccess(null);
    onClose();
  };

  const canSubmit = form.name.trim() && form.phone.trim() && form.labBrand
    && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/sales-reps`,
        { name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(), lab_brand: form.labBrand },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess({
        email: res.data?.email || form.email.trim(),
        emailSent: res.data?.email_sent !== false,
        tempPassword: res.data?.temp_password || null,
      });
      onCreated();
    } catch (err) {
      setError(extractErrorDetail(err, "Could not create this sales representative account."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>New Sales Representative</DialogTitle>
      <DialogContent>
        {success ? (
          success.emailSent ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Account created for <strong>{success.email}</strong>. Welcome email with temporary login
              credentials sent.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Account created for <strong>{success.email}</strong>, but the welcome email could not be
              sent. Hand this temporary password to them directly:
              <Box component="code" sx={{ display: "block", mt: 1, fontWeight: 700 }}>{success.tempPassword}</Box>
            </Alert>
          )
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Name" value={form.name} fullWidth autoFocus
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField label="Phone" value={form.phone} fullWidth
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <TextField label="Email" type="email" value={form.email} fullWidth
              helperText="They'll receive a temporary password and finish setting up their own account."
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <FormControl fullWidth>
              <InputLabel id="sales-rep-lab-brand-label">Lab Brand</InputLabel>
              <Select
                labelId="sales-rep-lab-brand-label" label="Lab Brand" value={form.labBrand}
                onChange={(e) => setForm({ ...form, labBrand: e.target.value })}
              >
                {LAB_BRAND_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Determines which lab this rep's clinicians (and their orders/reports) are routed to — can't be changed later.
              </FormHelperText>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {success ? (
          <Button onClick={handleClose} variant="contained" sx={{ bgcolor: BRAND.navy }}>Done</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit || submitting}
              sx={{ bgcolor: BRAND.navy }}>
              {submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Create Account"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default function AdminSalesReps() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const admin = JSON.parse(localStorage.getItem("admin") || "null");

  useEffect(() => {
    const fetchReps = async () => {
      try {
        const res = await axios.get(`${API_BASE}/admin/users`, {
          headers: { Authorization: `Bearer ${admin?.token}` },
          params: { page_size: 500 },
        });
        const all = res.data.users || [];
        setReps(all.filter((u) => u.role === "sales_rep"));
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem("admin");
          navigate("/admin-login", { replace: true });
          return;
        }
        setError(extractErrorDetail(err, "Failed to load sales representatives."));
      } finally {
        setLoading(false);
      }
    };
    fetchReps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleLogout = () => {
    localStorage.removeItem("admin");
    navigate("/admin-login", { replace: true });
  };

  const bg = isDark ? "#080d1a" : "#f4f7ff";

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg }}>
      <Box sx={{ bgcolor: "rgba(0,14,36,0.96)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(201,169,110,0.2)" }}>
        <Box sx={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(201,169,110,0.8) 30%, rgba(223,192,138,0.9) 50%, rgba(201,169,110,0.8) 70%, transparent)" }} />
        <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 1, minHeight: 60,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}>
          <img src="/logo-labarium-dark.png" alt="Labarium" style={{ height: 38, width: "auto" }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ThemeToggle />
            <Tooltip title="Log out">
              <IconButton size="small" onClick={handleLogout}
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" } }}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 5 }, py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", rowGap: 1 }}>
          <Box>
            <Button size="small" startIcon={<ArrowBackIcon fontSize="small" />}
              onClick={() => navigate("/admin-dashboard")}
              sx={{ textTransform: "none", fontWeight: 600, color: "#888", mb: 0.5 }}>
              User Dashboard
            </Button>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: "0.12em", color: BRAND.gold, mb: 0.5 }}>
              ADMIN
            </Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy }}>
              Sales Representatives
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}
            sx={{ bgcolor: BRAND.navy, textTransform: "none", fontWeight: 700 }}>
            New Sales Rep
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {reps.length === 0 && !error ? (
          <Alert severity="info">No sales representatives yet. Use "New Sales Rep" to create the first account.</Alert>
        ) : (
          <TableContainer sx={{ border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8", borderRadius: 2, overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                  {["Name", "Email", "Phone", "Lab Brand", "Created"].map((h) => (
                    <TableCell key={h} sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#555" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reps.map((r) => (
                  <TableRow key={r.sub} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAFA" } }}>
                    <TableCell sx={{ fontSize: "0.85rem", color: isDark ? "#e5e7eb" : "inherit" }}>{r.name || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{r.email}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>{r.phone || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                      {r.lab_brand_display || "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                      {r.created_at ? formatEST(r.created_at, { dateOnly: true }) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* 2026-10-23 fix (ui-ux-checker finding) — onCreated must NOT close
          the dialog itself (it used to), or the success/warning Alert
          (including a temp password shown when the welcome email fails)
          never has a chance to render before vanishing. Same pattern as
          AdminClinicians.js's own NewClinicianDialog — the admin
          dismisses via the dialog's own "Done" button. */}
      <NewSalesRepDialog open={dialogOpen} onClose={() => setDialogOpen(false)} token={admin?.token}
        onCreated={() => setRefreshKey((k) => k + 1)} />
    </Box>
  );
}
